import React, { useCallback, useMemo } from 'react';
import { showConfirm } from '@/utils/common/alert';
import { StackScreenProps } from '@react-navigation/stack';

import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { DESKTOP_CONFIRM_COPY } from '@/constants/admin';
import { useCreateProperty } from '@/hooks/admin/useCreateProperty';
import {
  PropertyGeneralInfoSection,
  PropertyPMSTypeSection,
  PropertyYardiSection,
  PropertyCardConnectSection,
  PropertyDetailsSection,
  PropertyFeesSection,
} from '@/components/admin/properties/form-sections';
import { MobileFormScreen } from '@/components/common/admin/mobile/MobileFormScreen';

type CreatePropertyScreenProps = StackScreenProps<AdminStackParamList, 'CreateProperty'>;

export const CreatePropertyScreen: React.FC<CreatePropertyScreenProps> = ({
  navigation,
}) => {
  const {
    formData,
    errors,
    isLoading,
    handleInputChange,
    handleSubmit,
  } = useCreateProperty({
    visible: true,
    onClose: () => navigation.goBack(),
  });

  // Double-confirm discard on cancel
  const confirmDiscardTwice = useCallback(async (onConfirm: () => void) => {
    const confirmed = await showConfirm(
      DESKTOP_CONFIRM_COPY.discardTitle,
      DESKTOP_CONFIRM_COPY.discardMessage,
      DESKTOP_CONFIRM_COPY.discardConfirmText,
      DESKTOP_CONFIRM_COPY.discardCancelText
    );

    if (confirmed) {
      onConfirm();
    }
  }, []);

  const handleCancel = useCallback(() => {
    confirmDiscardTwice(() => navigation.goBack());
  }, [confirmDiscardTwice, navigation]);

  // Memoized scroll content to prevent unnecessary re-renders
  const scrollContent = useMemo(() => (
    <>
      <PropertyGeneralInfoSection
        marketingName={formData.marketingName}
        externalId={formData.externalId}
        organizationId={formData.organizationId}
        nameError={errors.marketingName}
        externalIdError={errors.externalId}
        organizationIdError={errors.organizationId}
        onMarketingNameChange={(value) => handleInputChange('marketingName', value)}
        onExternalIdChange={(value) => handleInputChange('externalId', value)}
        onOrganizationIdChange={(value) => handleInputChange('organizationId', value)}
        disabled={isLoading}
      />

      <PropertyPMSTypeSection
        pmsType={formData.pmsType}
        onPmsTypeChange={(value) => handleInputChange('pmsType', value)}
        disabled={isLoading}
      />

      {formData.pmsType === 'yardi' && (
        <PropertyYardiSection
          yardiDomain={formData.yardiDomain}
          yardiUsername={formData.yardiUsername}
          yardiPassword={formData.yardiPassword}
          yardiServerName={formData.yardiServerName}
          yardiDatabase={formData.yardiDatabase}
          yardiBpInterfaceEntity={formData.yardiBpInterfaceEntity}
          yardiBpInterfaceLicense={formData.yardiBpInterfaceLicense}
          onYardiDomainChange={(value) => handleInputChange('yardiDomain', value)}
          onYardiUsernameChange={(value) => handleInputChange('yardiUsername', value)}
          onYardiPasswordChange={(value) => handleInputChange('yardiPassword', value)}
          onYardiServerNameChange={(value) => handleInputChange('yardiServerName', value)}
          onYardiDatabaseChange={(value) => handleInputChange('yardiDatabase', value)}
          onYardiBpInterfaceEntityChange={(value) => handleInputChange('yardiBpInterfaceEntity', value)}
          onYardiBpInterfaceLicenseChange={(value) => handleInputChange('yardiBpInterfaceLicense', value)}
          disabled={isLoading}
        />
      )}

      <PropertyCardConnectSection
        creditCardMerchantAccountId={formData.creditCardMerchantAccountId}
        achMerchantAccountId={formData.achMerchantAccountId}
        onCreditCardChange={(value) => handleInputChange('creditCardMerchantAccountId', value)}
        onAchChange={(value) => handleInputChange('achMerchantAccountId', value)}
        disabled={isLoading}
      />

      <PropertyDetailsSection
        propertyEmailAddress={formData.propertyEmailAddress}
        propertyPhoneNumber={formData.propertyPhoneNumber}
        propertyUrl={formData.propertyUrl}
        propertyFaqUrl={formData.propertyFaqUrl}
        propertyResidentHandbook={formData.propertyResidentHandbook}
        propertyCbxFilePath=""
        emailError={errors.propertyEmailAddress}
        phoneError={errors.propertyPhoneNumber}
        urlError={errors.propertyUrl}
        onEmailChange={(value) => handleInputChange('propertyEmailAddress', value)}
        onPhoneChange={(value) => handleInputChange('propertyPhoneNumber', value)}
        onUrlChange={(value) => handleInputChange('propertyUrl', value)}
        onFaqUrlChange={(value) => handleInputChange('propertyFaqUrl', value)}
        onHandbookChange={(value) => handleInputChange('propertyResidentHandbook', value)}
        onCbxFilePathChange={() => {}}
        disabled={isLoading}
      />

      <PropertyFeesSection
        propertyNsfFee={formData.propertyNsfFee}
        propertyLateDayStart={formData.propertyLateDayStart}
        propertyLateDayFee={formData.propertyLateDayFee}
        propertyLateDailyFee={formData.propertyLateDailyFee}
        propertyMaxMonthlyFee={formData.propertyMaxMonthlyFee}
        nsfFeeError={errors.propertyNsfFee}
        lateDayFeeError={errors.propertyLateDayFee}
        lateDailyFeeError={errors.propertyLateDailyFee}
        maxMonthlyFeeError={errors.propertyMaxMonthlyFee}
        onNsfFeeChange={(value) => handleInputChange('propertyNsfFee', value)}
        onLateDayStartChange={(value) => handleInputChange('propertyLateDayStart', value)}
        onLateDayFeeChange={(value) => handleInputChange('propertyLateDayFee', value)}
        onLateDailyFeeChange={(value) => handleInputChange('propertyLateDailyFee', value)}
        onMaxMonthlyFeeChange={(value) => handleInputChange('propertyMaxMonthlyFee', value)}
        disabled={isLoading}
      />
    </>
  ), [formData, errors, handleInputChange, isLoading]);

  return (
    <MobileFormScreen
      screenName="CreateProperty"
      navigation={navigation}
      isSubmitting={isLoading}
      submitText="Create"
      onSubmit={handleSubmit}
      onCancel={handleCancel}
    >
      {scrollContent}
    </MobileFormScreen>
  );
};
